import { useEffect, useState } from "react";
import { EntityId } from "@reduxjs/toolkit";

import { useNavigate } from "react-router-dom";

import { DateTime, DurationObjectUnits } from "luxon";

import {
    Button,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Stack,
    InputLabel,
    ButtonGroup,
    Typography,
} from "@mui/material";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import TuneIcon from "@mui/icons-material/Tune";
import TheatersIcon from "@mui/icons-material/Theaters";
import DoorbellIcon from "@mui/icons-material/Doorbell";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import LoginIcon from "@mui/icons-material/Login";
import LoadingButton from "@mui/lab/LoadingButton";
import { useConfirm } from "material-ui-confirm";

import {
    useGetAudioMutedQuery,
    useUpdateAudioMutedMutation,
} from "services/streamingApi/siteCommands.api";

import { unwrappableActionSnackbarNotificationDecorator } from "utils/unwrappableActionSnackbarNotificationDecorator";
import { PortTypeSectionList } from "./PortTypeSectionList";
import { enqueueSnackbar } from "notistack";
import { MatchType } from "features/appFeatures/types/matchType";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { luxonUtils } from "utils/luxonUtils";
import { MediaClipType } from "types/MediaClipType";
import { PlayMediaClipButton } from "./PlayMediaClipButton";
import { selectSiteById } from "features/sites/store/selectors";
import { useAppSelector } from "app/hooks";
import { CountryCodeType } from "types/CountryCodeType";

export interface SiteControlsProps {
    siteId: EntityId;
    hidePlaybackButtons?: boolean;
}

export function SiteControls(props: SiteControlsProps) {
    const confirm = useConfirm();

    // router navigation
    const navigate = useNavigate();

    const site = useAppSelector((state) =>
        selectSiteById(state, props.siteId.toString()),
    );

    const isFrenchSite =
        site?.primaryAddress?.countryCode === CountryCodeType.France;

    // site mute state
    const {
        data: isSiteMuted,
        isFetching: isLoadingSiteMuted,
        isError: isErrorSiteMuted,
    } = useGetAudioMutedQuery(props.siteId, {
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        if (isErrorSiteMuted) {
            enqueueSnackbar(`Failed get mute state of club`, {
                variant: "error",
            });
        }
    }, [isErrorSiteMuted]);

    // change site mute state
    const [updateAudioMuted, { isLoading: isLoadingUpdateAudioMuted }] =
        useUpdateAudioMutedMutation();

    // collapsable list
    const [open, setOpen] = useState(true);

    const handleClick = () => {
        setOpen(!open);
    };

    // url creation playback
    const createPlaybackWithRangeNavigateCallback = (
        timeAmount: number,
        unitOfTime: keyof DurationObjectUnits,
    ) => {
        return () => {
            const endDateTime = luxonUtils.resetSecondsAndMilliseconds(
                DateTime.now(),
            );
            const startDateTime = endDateTime.minus({
                [unitOfTime]: timeAmount,
            });

            navigate(
                `/playback/${props.siteId}?start=${encodeURIComponent(startDateTime.toISO() as string)}&end=${encodeURIComponent(endDateTime.toISO() as string)}`,
            );
        };
    };

    return (
        <>
            <ListItemButton onClick={handleClick}>
                <ListItemIcon>
                    <TuneIcon />
                </ListItemIcon>
                <ListItemText primary="Site controls" />
                {open ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse
                in={open}
                timeout="auto"
                unmountOnExit
                sx={{
                    padding: 2,
                }}
            >
                <Stack spacing={2}>
                    {!props.hidePlaybackButtons && (
                        <FeatureFlagGuard
                            accessibleWithFeatureFlags={[AppFeature.Playback]}
                        >
                            <Stack spacing={1}>
                                <InputLabel variant="standard">
                                    Playback
                                </InputLabel>
                                <ButtonGroup
                                    size="small"
                                    variant="outlined"
                                    aria-label="outlined primary button group"
                                >
                                    {/* We need to use onClick of buttons untill this is fixed: https://github.com/mui/material-ui/issues/29514 */}
                                    <Button
                                        onClick={createPlaybackWithRangeNavigateCallback(
                                            15,
                                            "minutes",
                                        )}
                                    >
                                        <Typography>15 min</Typography>
                                    </Button>
                                    <Button
                                        onClick={createPlaybackWithRangeNavigateCallback(
                                            30,
                                            "minutes",
                                        )}
                                    >
                                        <Typography variant="buttonCondensed">
                                            30 min
                                        </Typography>
                                    </Button>
                                    <Button
                                        onClick={createPlaybackWithRangeNavigateCallback(
                                            1,
                                            "hours",
                                        )}
                                    >
                                        <Typography variant="buttonCondensed">
                                            1 hour
                                        </Typography>
                                    </Button>
                                    <Button
                                        onClick={() =>
                                            navigate(
                                                `/playback/${props.siteId}`,
                                            )
                                        }
                                    >
                                        <TheatersIcon />
                                    </Button>
                                </ButtonGroup>
                            </Stack>
                        </FeatureFlagGuard>
                    )}

                    <FeatureFlagGuard
                        accessibleWithFeatureFlags={[
                            AppFeature.SiteOperationsMuteAudio,
                            AppFeature.SiteOperationsDingDong,
                        ]}
                        matchType={MatchType.Any}
                    >
                        <Stack spacing={1}>
                            <InputLabel>Audio</InputLabel>
                            <FeatureFlagGuard
                                accessibleWithFeatureFlags={[
                                    AppFeature.SiteOperationsDingDong,
                                ]}
                            >
                                <PlayMediaClipButton
                                    siteId={props.siteId}
                                    mediaClipType={MediaClipType.DingDong}
                                    icon={<DoorbellIcon />}
                                    buttonText="Ring doorbell"
                                    successMessage="Successfully rang doorbell"
                                    failureMessage="Failed to ring doorbell"
                                />
                            </FeatureFlagGuard>

                            {isFrenchSite && (
                                <FeatureFlagGuard
                                    accessibleWithFeatureFlags={[
                                        AppFeature.SiteOperationsKioskCheckIn,
                                    ]}
                                >
                                    <PlayMediaClipButton
                                        siteId={props.siteId}
                                        mediaClipType={
                                            MediaClipType.KioskCheckIn
                                        }
                                        icon={<LoginIcon />}
                                        buttonText="Kiosk check-in"
                                        successMessage="Successfully played kiosk check-in audio"
                                        failureMessage="Failed to play kiosk check-in audio"
                                    />
                                </FeatureFlagGuard>
                            )}

                            <FeatureFlagGuard
                                accessibleWithFeatureFlags={[
                                    AppFeature.SiteOperationsMuteAudio,
                                ]}
                            >
                                <LoadingButton
                                    sx={{
                                        "&.Mui-disabled": {
                                            color: "#2196f3",
                                        },
                                    }}
                                    variant={
                                        isSiteMuted ? "contained" : "outlined"
                                    }
                                    color={isSiteMuted ? "error" : "success"}
                                    startIcon={
                                        isSiteMuted ? (
                                            <VolumeOffIcon />
                                        ) : (
                                            <VolumeUpIcon />
                                        )
                                    }
                                    loading={
                                        isLoadingSiteMuted ||
                                        isLoadingUpdateAudioMuted
                                    }
                                    loadingPosition="start"
                                    disabled={isErrorSiteMuted}
                                    onClick={() => {
                                        confirm({
                                            description: `Are you sure you want to turn ${!isSiteMuted ? "off" : "on"} the music in the club?`,
                                            confirmationText: "Yes",
                                            cancellationText: "No",
                                        }).then((confirmResult) => {
                                            if (confirmResult.confirmed) {
                                                unwrappableActionSnackbarNotificationDecorator(
                                                    updateAudioMuted({
                                                        siteId: props.siteId,
                                                        isMuted: !isSiteMuted,
                                                    }),
                                                    `Successfully turned music ${!isSiteMuted ? "off" : "on"}`,
                                                    `Failed to turn music ${!isSiteMuted ? "off" : "on"}`,
                                                );
                                            }
                                        });
                                    }}
                                >
                                    Club Music {isSiteMuted ? "off" : "on"}
                                </LoadingButton>
                            </FeatureFlagGuard>
                        </Stack>
                    </FeatureFlagGuard>

                    <PortTypeSectionList siteId={props.siteId} />
                </Stack>
            </Collapse>
        </>
    );
}
